import {
    Component,
    OnInit,
    HostListener,
    ChangeDetectorRef,
    ElementRef,
    ViewChild,
    AfterViewInit,
    EventEmitter,
    Input,
    Output,
    OnDestroy,
} from "@angular/core"
import { PlayerService } from "../../services/audio/player.service"
import { Router } from "@angular/router"
import { ApiService } from "../../services/api.service"
import { ShortcutsService } from "../../services/shortcuts.service"
import { Misc } from "../../../../../common-lib/general/modules/misc"
import * as Hammer from "hammerjs"
import { Observable } from "rxjs"
import { ParentClass } from "../../parent"

@Component({
    selector: "app-player",
    templateUrl: "player.component.html",
    styleUrls: ["player.component.scss"],
})
export class PlayerComponent
    extends ParentClass
    implements OnInit, AfterViewInit, OnDestroy
{
    @Input() public showLyricsButton: boolean = false
    @Input() public lyricsPanelOpen: boolean = false
    @Output() public lyricsPanelToggle = new EventEmitter<void>()

    durationText: string = "0:00"
    currentTimeText: string = "0:00"

    get isPlaying(): boolean {
        return this.playerService.query.isPlaying
    }

    get isPlaying$(): Observable<boolean> {
        return this.playerService.query.isPlaying$
    }

    isLoading: boolean = false

    progressHovered = false

    progress: number = 0

    isLoggedIn = false
    publicPlayer = false

    loadingProgress: number = 0
    currentLyrics: string = ""

    mobileMaximized: boolean = false

    @ViewChild("playerCanvas") canvas?: ElementRef<HTMLCanvasElement>

    @ViewChild("maxPlayer") maxPlayer: ElementRef
    playerBarHeight: number

    private lastTimeElapsed: number = 0
    private hammerManagers: Array<{ destroy: () => void }> = []

    constructor(
        public playerService: PlayerService,
        private shortcutSevice: ShortcutsService,
        private ref: ChangeDetectorRef,
        private apiService: ApiService,
        private router: Router
    ) {
        super()
    }

    ngOnInit() {
        this.subscribe(this.playerService.query.timeElapsed$, timeElapsed => {
            this.setSongMetadata(timeElapsed)

            if (Math.floor(this.lastTimeElapsed) !== Math.floor(timeElapsed)) {
                this.drawRMSEnv(this.playerService.query.audioBufferForRMS)

                this.lastTimeElapsed = timeElapsed
            }
        })

        this.subscribe(this.playerService.query.currentSong$, song => {
            this.setSongMetadata(this.playerService.query.timeElapsed)

            const lyrics = song?.song.lyrics
            this.currentLyrics =
                typeof lyrics === "string" && lyrics.trim().length > 0
                    ? lyrics
                    : ""
        })

        this.subscribe(
            this.playerService.query.audioBufferForRMS$,
            dataForRMS => {
                if (
                    dataForRMS.data === undefined ||
                    this.playerService.query.currentSong === null
                ) {
                    return
                }

                this.drawRMSEnv(dataForRMS)
            }
        )
    }

    private drawRMSEnv(dataForRMS: { endTime: number; data: AudioBuffer }) {
        const canvas = this.canvas?.nativeElement

        if (dataForRMS.data === undefined || canvas === undefined) {
            return
        }

        const targetNbOfSamples = 150

        const rmsWindowSize =
            (this.playerService.query.currentSong.song.duration *
                PlayerService.SAMPLE_RATE) /
            targetNbOfSamples

        const data = this.calculateRMS(
            dataForRMS.data.getChannelData(0),
            rmsWindowSize
        )

        const context = canvas.getContext("2d")

        if (context === null) {
            return
        }

        context.clearRect(0, 0, canvas.width, canvas.height)

        data.forEach((value, index) => {
            const frameWidth =
                this.getWidthForFrame(targetNbOfSamples) * canvas.width
            const x = frameWidth / 2 + frameWidth * 2 * index

            const height = Math.max(1, value * canvas.height)
            const heightReminder = canvas.height - height

            this.drawRoundedRectangle(
                context,
                x,
                heightReminder / 2,
                frameWidth,
                canvas.height - heightReminder,
                1,
                (rmsWindowSize * index) / PlayerService.SAMPLE_RATE <
                    this.playerService.query.timeElapsed
            )
        })
    }

    private drawRoundedRectangle(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        w: number,
        h: number,
        r: number,
        frameWasPlayed: boolean
    ) {
        if (frameWasPlayed) {
            ctx.fillStyle = "#ffffff"
        } else {
            ctx.fillStyle = "rgba(255,255,255,0.4)"
        }
        ctx.imageSmoothingQuality = "high"
        ctx.imageSmoothingEnabled = true

        ctx.beginPath()
        ctx.moveTo(x + r, y)
        ctx.arcTo(x + w, y, x + w, y + h, r)
        ctx.arcTo(x + w, y + h, x, y + h, r)
        ctx.arcTo(x, y + h, x, y, r)
        ctx.arcTo(x, y, x + w, y, r)

        ctx.fill("evenodd")
        ctx.closePath()
    }

    private drawHorizontalLine(
        ctx: CanvasRenderingContext2D,
        x: number,
        height: number,
        width: number
    ) {
        ctx.strokeStyle = "white"
        ctx.beginPath()
        ctx.rect(x, height, width, 5)
        ctx.stroke()
    }

    public getLoadingBufferWidth(rmsDataEnd: number): number {
        return this.playerService.query.currentSong !== null
            ? (rmsDataEnd /
                  this.playerService.query.currentSong.song.duration) *
                  100
            : 0
    }

    public getWidthForFrame(nbOfSamples: number): number {
        return 1 / nbOfSamples / 2
    }

    private calculateRMS(
        audioData: Float32Array,
        windowSizeInSamples: number = 44100
    ): number[] {
        const rmsValues: number[] = []

        for (let i = 0; i < audioData.length; i += windowSizeInSamples) {
            const maxValue = Math.max(
                ...audioData.slice(i, i + windowSizeInSamples)
            )

            rmsValues.push(maxValue)
        }

        const maxRMS = Math.max(...rmsValues)
        const normalizedRMSValues = rmsValues.map(value => value / maxRMS)

        return normalizedRMSValues
    }

    private setSongMetadata(timeElapsed: number) {
        if (this.playerService.query.currentSong === null) {
            this.currentTimeText = "0:00"
            this.durationText = "0:00"
            this.progress = 0
            return
        }

        const duration = this.playerService.query.currentSong.song.duration
        const safeDuration =
            Number.isFinite(duration) && duration > 0 ? duration : 0
        const safeTime =
            Number.isFinite(timeElapsed) && timeElapsed > 0
                ? Math.min(timeElapsed, safeDuration || timeElapsed)
                : 0

        this.currentTimeText = Misc.convertSecondsToString(safeTime)
        this.progress = safeDuration
            ? Math.max(0, Math.min(100, (safeTime * 100) / safeDuration))
            : 0
        this.durationText = Misc.convertSecondsToString(safeDuration)
    }

    private setupCanvas() {
        const canvas = this.canvas?.nativeElement

        if (canvas === undefined || canvas.parentElement === null) {
            return
        }

        const ctx = canvas.getContext("2d")

        if (ctx === null) {
            return
        }

        canvas.width = canvas.parentElement.clientWidth
        canvas.height = canvas.parentElement.clientHeight

        const dpi = window.devicePixelRatio || 1

        ctx.canvas.width = canvas.width * dpi
        ctx.canvas.height = canvas.height * dpi
    }

    ngAfterViewInit() {
        this.setupCanvas()

        this.playerBarHeight = this.getPlayerBarHeight()

        this.subscribe(this.apiService.isLoggedIn, value => {
            this.isLoggedIn = value

            if (!value) {
                void this.playerService.teardown()
            }

            this.ref.detectChanges()
        })

        this.initializeSwipeGestures()
    }

    getPlayerBarHeight() {
        const rootStyles = getComputedStyle(document.documentElement)
        const playerBarHeight = rootStyles
            .getPropertyValue("--player-bar-height")
            .trim()

        return parseInt(playerBarHeight, 10)
    }

    // Swipe gestures
    initializeSwipeGestures() {
        const playerElements = document.querySelectorAll(".player")

        playerElements.forEach(element => {
            const hammer = new Hammer(element)
            this.hammerManagers.push(hammer)
            hammer.get("pan").set({ direction: Hammer.DIRECTION_VERTICAL })

            hammer.on("panstart", ev => {
                this.handleSwipeStart(ev)
            })

            hammer.on("panmove", ev => {
                this.handleSwipeMove(ev)
            })

            hammer.on("panend", ev => {
                this.handleSwipeEnd(ev)
            })
        })
    }

    handleSwipeStart(event) {
        // Remove the transition duration so swipe movement is smooth and contineous
        this.maxPlayer.nativeElement.style.transitionDuration = "0s"

        if (!this.mobileMaximized) {
            this.maxPlayer.nativeElement.style.transform = `translateY(${
                window.innerHeight - this.playerBarHeight
            }px)`
        }
    }

    handleSwipeMove(event) {
        if (event.center.y < 0) {
            return
        }

        const translateY = this.mobileMaximized
            ? Math.max(0, event.deltaY)
            : event.center.y

        this.maxPlayer.nativeElement.style.transform = `translateY(${translateY}px)`
    }

    handleSwipeEnd(event) {
        this.maxPlayer.nativeElement.style.transitionDuration = "300ms"

        const isQuickSwipeGesture =
            Math.abs(event.velocityY) > 0.1 &&
            (event.distance < 200 || Math.abs(event.deltaTime) < 500)

        if (isQuickSwipeGesture) {
            if (event.velocityY > 0) {
                this.minimizePlayer()
            } else {
                this.maximizePlayer()
            }
        } else {
            const threshold = window.innerHeight / 2
            const clientY = event.srcEvent.clientY

            if (clientY > threshold) {
                this.minimizePlayer()
            } else {
                this.maximizePlayer()
            }
        }
    }

    minimizePlayer() {
        this.maxPlayer.nativeElement.style.transform = ""
        this.mobileMaximized = false
    }

    maximizePlayer() {
        this.maxPlayer.nativeElement.style.transform = ""
        this.mobileMaximized = true
    }

    showPlayer() {
        return this.isLoggedIn
    }

    @HostListener("document:keydown", ["$event"])
    async handleKeyboardEvent(event: KeyboardEvent) {
        const target = (event.target ??
            document.activeElement) as HTMLElement | null
        const tagName = target?.tagName
        if (
            tagName === "INPUT" ||
            tagName === "TEXTAREA" ||
            tagName === "SELECT" ||
            target?.isContentEditable
        ) {
            return
        }

        if (event.key == " " && !this.shortcutSevice.shortcutPrevention()) {
            if (!this.isPlaying && !this.isLoading) {
                await this.play()
            } else {
                await this.pause()
            }

            event.preventDefault()
        }
    }

    play() {
        return this.playerService.play()
    }

    pause() {
        return this.playerService.pause()
    }

    next() {
        return this.playerService.next()
    }

    previous() {
        return this.playerService.previous()
    }

    seek(event: MouseEvent): void {
        const target = event.currentTarget as HTMLElement | null
        if (target === null) {
            return
        }

        const rect = target.getBoundingClientRect()
        if (!(rect.width > 0)) {
            return
        }

        const percentage = ((event.clientX - rect.left) / rect.width) * 100
        this.playerService.seek(Math.max(0, Math.min(100, percentage)))
    }

    switchToMyTracks(): void {
        this.router.navigate(["/"])
    }

    public toggleLyricsPanel(): void {
        if (this.playerService.query.currentSong === null) {
            return
        }

        this.lyricsPanelToggle.emit()
    }

    override ngOnDestroy(): void {
        for (const hammer of this.hammerManagers) {
            hammer.destroy()
        }
        this.hammerManagers = []
        super.ngOnDestroy()
    }
}
